import { ReactNode } from 'react';

import { Field, FieldInputProps } from 'react-final-form';

import { RadioInputComplexLabel as Input } from '@vk-hr-tek/ui/input';

import { composeValidators, ValidateType } from '../validation';

/**
 * Функция обработчик изменения значения радиокнопки.
 *
 * @param input - Объект с инпут-пропсами из react-final-form.
 * @param onChange - Дополнительная функция обратного вызова при изменении значения.
 * @returns Функция изменения значения, которая вызывает обновление инпута и дополнительный обработчик при его наличии.
 */
const handleChange =
  (
    input: FieldInputProps<string | undefined, HTMLElement>,
    onChange?: (value?: string | number) => void,
  ) =>
  (value?: string | number) => {
    input.onChange(value);

    if (onChange) {
      onChange(value);
    }
  };

/**
 * Интерфейс пропсов компонента {@link RadioInputComplexLabel}.
 *
 * @typeParam T - Тип метки для опций радиокнопок.
 */
interface RadioInputComplexLabelProps<T> {
  /**
   * Имя поля формы.
   */
  name: string;

  /**
   * Функция обратного вызова при изменении значения.
   */
  onChange?: (value?: string | number) => void;

  /**
   * Значение по умолчанию для поля.
   */
  defaultValue?: string;

  /**
   * Массив опций для радиокнопок.
   */
  options: { value: string | number; label: T }[];

  /**
   * Функция рендера метки.
   */
  render: (item: T) => ReactNode;

  /**
   * Функция рендера футера для опции.
   */
  renderFooter?: (item: T, isSelected: boolean) => ReactNode;

  /**
   * Вариант стиля радиокнопок.
   *
   * @defaultValue `'primary'`
   */
  variant?: 'primary' | 'secondary';

  /**
   * Label для RadioInputComplexLabel.
   */
  label?: string;

  /**
   * Указывает, является ли группа радиокнопок обязательной.
   */
  required?: boolean;

  /**
   * Флаг, разрешающий очистку выбора.
   */
  allowClear?: boolean;

  /**
   * Массив правил валидации.
   */
  validateArray?: (ValidateType | undefined)[];
}

/**
 * Компонент радиокнопок с сложными метками.
 *
 * @public
 * @typeParam T - Тип метки для опций радиокнопок.
 * @param name - Имя поля формы.
 * @param onChange - Функция обратного вызова при изменении значения.
 * @param options - Массив опций для радиокнопок.
 * @param defaultValue - Значение по умолчанию для поля.
 * @param renderFooter - Функция рендера футера для опции.
 * @param render - Функция рендера метки.
 * @param variant - Вариант стиля радиокнопок.
 * @param label - Label для RadioInputComplexLabel.
 * @param required - Указывает, является ли группа радиокнопок обязательной.
 * @param allowClear - Флаг, разрешающий очистку выбора.
 * @param validateArray - Массив правил валидации.
 * @param rest - Остальные пропсы, передаваемые в {@link Field}.
 * @returns Рендерит компонент радиокнопок или null, если опций нет.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link RadioInputComplexLabelProps}
 */
export const RadioInputComplexLabel = <T,>({
  name,
  onChange,
  options,
  defaultValue,
  renderFooter,
  render,
  variant = 'primary',
  label,
  required,
  allowClear,
  validateArray = [],
  ...rest
}: RadioInputComplexLabelProps<T>) => {
  if (!options || !options.length) {
    return null;
  }

  return (
    <Field
      name={name}
      initialValue={defaultValue}
      validate={composeValidators(validateArray)}
      {...rest}
    >
      {({ input }) => (
        <Input
          {...input}
          items={options}
          onChange={handleChange(input, onChange)}
          render={render}
          renderFooter={renderFooter}
          variant={variant}
          label={label}
          required={required}
          allowClear={allowClear}
        />
      )}
    </Field>
  );
};
